import { useEffect, useState, useCallback } from "react";
import Modal from "shared/Modal";
import { Loader2 } from "lucide-react";
import { fetchAuditLog } from "../api/attendanceApi";

interface AuditLogValues {
  status: number;
  timestamp: string | null;
}

interface AuditLogItem {
  id: number;
  attendance_raw_id: number | null;
  employee_code: string;
  attendance_date: string;
  action: "add" | "edit" | "delete";
  old_values: AuditLogValues | null;
  new_values: AuditLogValues | null;
  source: "admin_direct" | "hr_request";
  performed_by: string;
  requested_by: string | null;
  note: string | null;
  created_at: string;
}

interface Props {
  onClose: () => void;
}

const ACTION_LABELS: Record<string, string> = { add: "Added", edit: "Edited", delete: "Removed" };

function fmtValues(v: AuditLogValues | null) {
  if (!v || !v.timestamp) return "—";
  return `${v.status === 0 ? "Check In" : "Check Out"} @ ${v.timestamp.slice(11, 16)}`;
}

export default function AuditLogPanel({ onClose }: Props) {
  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [employeeCode, setEmployeeCode] = useState("");

  const load = useCallback(async (code: string) => {
    setLoading(true);
    try {
      const data = await fetchAuditLog(code || undefined);
      setItems(data.items || []);
    } catch (err) {
      console.error("Failed to load audit log:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal size="lg" onClick={onClose} closeButtonCLick={onClose} modalClassName="p-6">
      <h2 className="text-lg font-semibold text-foreground font-inter mb-4">Attendance Audit Log</h2>

      <div className="flex gap-2 mb-4">
        <input
          value={employeeCode}
          onChange={(e) => setEmployeeCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(employeeCode)}
          placeholder="Filter by employee code"
          className="px-3 py-2 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring flex-1"
        />
        <button
          onClick={() => load(employeeCode)}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
        >
          Filter
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground font-inter text-center py-10">No changes logged.</p>
      ) : (
        <div className="space-y-3">
          {items.map((log) => (
            <div key={log.id} className="border border-border rounded-xl p-3 text-sm font-inter">
              <p className="text-foreground font-medium">
                {ACTION_LABELS[log.action]} · {log.employee_code} · {log.attendance_date}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {log.old_values && `${fmtValues(log.old_values)} → `}
                {fmtValues(log.new_values)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {log.source === "hr_request"
                  ? `Requested by ${log.requested_by ?? "—"}, approved by ${log.performed_by}`
                  : `By ${log.performed_by}`}
                {" · "}
                {new Date(log.created_at).toLocaleString()}
              </p>
              {log.note && <p className="text-xs text-muted-foreground mt-1">"{log.note}"</p>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
